import { Injectable } from '@angular/core';

export interface WorkflowRecoveryRecord<T = unknown> {
  key: string;
  workflowId: string;
  userId: string;
  tenantId?: string;
  baselineVersion: number | string;
  savedAt: string;
  expiresAt: string;
  workingSnapshot: T;
}

const DATABASE_NAME = 'smartforms-workflow-recovery';
const STORE_NAME = 'recoveries';
export const WORKFLOW_RECOVERY_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class WorkflowRecoveryService {
  buildKey(userId: string, tenantId: string | undefined, workflowId: string, baselineVersion: number | string): string {
    return ['workflow-recovery-v1', userId, tenantId || 'default', workflowId, baselineVersion]
      .map(value => encodeURIComponent(String(value)))
      .join(':');
  }

  async put<T>(record: Omit<WorkflowRecoveryRecord<T>, 'key' | 'expiresAt'>): Promise<void> {
    const stored: WorkflowRecoveryRecord<T> = {
      ...record,
      key: this.buildKey(record.userId, record.tenantId, record.workflowId, record.baselineVersion),
      expiresAt: new Date(Date.now() + WORKFLOW_RECOVERY_RETENTION_MS).toISOString(),
    };
    const db = await this.open();
    await this.request(db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put(stored));
    db.close();
  }

  async find<T>(userId: string, tenantId: string | undefined, workflowId: string): Promise<WorkflowRecoveryRecord<T>[]> {
    const db = await this.open();
    const records = await this.request<WorkflowRecoveryRecord<T>[]>(
      db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll()
    );
    db.close();
    const now = Date.now();
    const matching = records.filter(record =>
      record.userId === userId &&
      (record.tenantId || '') === (tenantId || '') &&
      record.workflowId === workflowId
    );
    await Promise.all(records.filter(record => Date.parse(record.expiresAt) <= now).map(record => this.remove(record.key)));
    return matching.filter(record => Date.parse(record.expiresAt) > now)
      .sort((a, b) => Date.parse(b.savedAt) - Date.parse(a.savedAt));
  }

  async remove(key: string): Promise<void> {
    const db = await this.open();
    await this.request(db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(key));
    db.close();
  }

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME, { keyPath: 'key' });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private request<T = IDBValidKey | undefined>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}
